import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { connect } from 'redux-bundler-react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import React from 'react';
import * as yup from 'yup';
import PasswordTip from './PasswordTip';

import ConfirmationDialog from './ConfirmationDialog';

const styles = theme => ({
  inProgress: {
    display: 'flex',
    alignItems: 'center',
  },

  spinner: {
    marginLeft: theme.spacing(1),
  },
  requirement: {
    marginTop: theme.spacing(1),
    display: 'flex',
    color: theme.palette.text.secondary,

    alignItems: 'center',
  },
  requirementIcon: {
    marginRight: theme.spacing(1),
  },
});

const PasswordChangeDialog = ({
  classes,
  open,
  onClose,
  doChangePassword,
  activeClient,
  authenticatedUser,
}) => {
  const [password, setPassword] = React.useState('');
  const [passwordConfirmation, setPasswordConfirmation] = React.useState('');
  const [passwordMatches, setPasswordMatches] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [message, setMessage] = React.useState(false);

  const [isComplex, setComplex] = React.useState(false);
  const cua = authenticatedUser.clientUserAssignments.find(
    cua => cua.clientId === activeClient.id,
  );

  const username = cua?.username || '';

  React.useEffect(() => {
    setPasswordMatches(password && password === passwordConfirmation);
  }, [password, passwordConfirmation]);

  const handleConfirm = async (values, { setErrors, setSubmitting }) => {
    try {
      setBusy(true);
      const passwordChange = await doChangePassword(values.password);
      if (passwordChange.isPersisted) {
        setBusy(false);
        setShowConfirmation(true);
        setMessage('Your password has been succesfully changed');
        onClose();
        return;
      }
      const errors = mapErrorsToFormik(passwordChange.errors);
      console.log(errors);

      setErrors(errors);
      setSubmitting(false);
      setBusy(false);
    } catch (err) {
      setShowConfirmation(true);
      setMessage(
        'An error occurred updating your password. Please contact support',
      );

      setSubmitting(false);
      setBusy(false);
    }
  };

  const handleCheck = valid => {
    console.log(valid);
    setComplex(valid);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const mapErrorsToFormik = errors =>
    Object.keys(errors).reduce(
      (memo, key) => ({ ...memo, [key]: `${key} ${errors[key].message}` }),
      {},
    );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      open && inputEl.current.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [open]);

  const inputEl = React.useRef(null);

  return (
    <>
      <ConfirmationDialog
        title="Change Password"
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        message={message}
      />
      <Dialog
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        aria-labelledby="confirmation-dialog-title"
        open={open}
      >
        <Formik
          enableReinitialize
          initialValues={{
            password: '',
            passwordConfirmation: '',
          }}
          validationSchema={yup.object().shape({
            password: yup
              .string()
              .required('Password is a required field')
              .min(8)
              .test(
                'no-username',
                'Password cannot contain username',
                value => !value.toLowerCase().includes(username.toLowerCase()),
              ),
            passwordConfirmation: yup
              .string()
              .required()
              .oneOf([yup.ref('password'), null], 'Passwords must match'),
          })}
          onSubmit={handleConfirm}
          validateOnMount
        >
          {({ isValid, values }) => {
            return (
              <Form>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                  Change your password for all{' '}
                  <strong>{activeClient.name}</strong> workstations
                  <div>
                    <Field
                      name="password"
                      type="password"
                      fullWidth
                      component={TextField}
                      margin="normal"
                      variant="outlined"
                      label="Password"
                      autofocus
                      inputRef={inputEl}
                    />
                    Please ensure your password is 8 or more characters and
                    meets 3 of the other requirements:
                    <Grid container>
                      <Grid xs={6}>
                        <PasswordTip
                          check="length"
                          password={values.password}
                          onCheck={handleCheck}
                        >
                          8 or more characters
                        </PasswordTip>
                        <PasswordTip
                          check="uppercase"
                          password={values.password}
                        >
                          1 uppercase character
                        </PasswordTip>
                        <PasswordTip
                          check="lowercase"
                          password={values.password}
                        >
                          1 lowercase character
                        </PasswordTip>
                      </Grid>
                      <Grid xs={6}>
                        <PasswordTip
                          check="punctuation"
                          password={values.password}
                        >
                          1 punctuation character
                        </PasswordTip>
                        <PasswordTip check="number" password={values.password}>
                          1 number
                        </PasswordTip>
                        <PasswordTip check="special" password={values.password}>
                          1 special character
                        </PasswordTip>
                      </Grid>
                    </Grid>
                    <Field
                      name="passwordConfirmation"
                      type="password"
                      fullWidth
                      component={TextField}
                      margin="normal"
                      variant="outlined"
                      label="Confirm Password"
                    />
                  </div>
                  {busy && (
                    <Typography variant="body" className={classes.inProgress}>
                      Updating password...{' '}
                      <CircularProgress size={20} className={classes.spinner} />
                    </Typography>
                  )}
                </DialogContent>

                <DialogActions>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    type="submit"
                    disabled={!isValid || busy || !isComplex}
                  >
                    Change Password
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default connect(
  'doChangePassword',
  'selectActiveClient',
  'selectAuthenticatedUser',
  withStyles(PasswordChangeDialog, styles),
);
